import { useState, useEffect } from "react";
import { sb, SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";

// Settings → Texting. Phase 2 of operator messaging: walks the operator through getting
// A2P-10DLC approved so they can text clients. Surfaces their compliant URLs (the hosted
// Privacy/Terms/opt-in from phase 1), pre-fills compliant sample messages + opt-in copy,
// collects the business info Twilio TrustHub needs, and submits for registration.
const EMBED_HOST = "cryptohunting.world"; // renderer host (matches WebsiteSettings)
const authHeaders = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });

const BIZ_TYPES = [
  ["sole_proprietor", "Sole proprietor (no EIN)"],
  ["llc", "LLC"],
  ["corporation", "Corporation"],
  ["partnership", "Partnership"],
  ["non_profit", "Non-profit"],
];
const INDUSTRIES = ["Home & Field Services", "Professional Services", "Real Estate", "Construction", "Retail", "Other"];

// Module-level (stable identity) so editing a field never remounts the input.
function Field({ label, k, form, onSet, ph, type = "text", wide, hint }) {
  return (
    <div className={`a2p-f${wide ? " wide" : ""}`}>
      <label>{label}</label>
      <input value={form[k] || ""} onChange={e => onSet(k, e.target.value)} placeholder={ph} type={type} autoComplete="off" />
      {hint && <div className="a2p-hint">{hint}</div>}
    </div>
  );
}
function TextArea({ label, k, form, onSet, hint }) {
  return (
    <div className="a2p-f wide">
      <label>{label}</label>
      <textarea value={form[k] || ""} onChange={e => onSet(k, e.target.value)} rows={3} />
      {hint && <div className="a2p-hint">{hint}</div>}
    </div>
  );
}

function prefill(info, site, org) {
  const name = info.legal_business_name || site?.business_name || org?.name || "";
  const base = site?.subdomain ? `https://${site.subdomain}.${EMBED_HOST}` : "";
  return {
    legal_business_name: info.legal_business_name || site?.business_name || org?.name || "",
    business_type:       info.business_type || "sole_proprietor",
    ein:                 info.ein || "",
    business_industry:   info.business_industry || "Home & Field Services",
    address_street:      info.address_street || org?.address || "",
    address_city:        info.address_city || org?.city || "",
    address_state:       info.address_state || org?.state || "",
    address_postal:      info.address_postal || org?.zip || "",
    website_url:         info.website_url || base || "",
    rep_name:            info.rep_name || org?.contact_name || "",
    rep_email:           info.rep_email || site?.email || org?.email || "",
    rep_phone:           info.rep_phone || site?.phone || org?.phone || "",
    use_case_description: info.use_case_description || `Appointment confirmations, reminders, and technician on-the-way / ETA notifications for ${name || "our"} dog-waste removal service.`,
    sample_message_1:    info.sample_message_1 || `${name || "Business"}: Your appointment is confirmed for [date] at [time]. Reply STOP to opt out, HELP for help.`,
    sample_message_2:    info.sample_message_2 || `${name || "Business"}: Your technician is on the way, arriving in about [ETA] min. Reply STOP to unsubscribe.`,
    opt_in_description:  info.opt_in_description || `Customers opt in by checking an unchecked SMS consent box on our online sign-up form${base ? ` at ${base}` : ""}. The form states the message types, that frequency may vary, that message and data rates may apply, and how to reply STOP to opt out or HELP for help, with links to our Privacy Policy and Terms.`,
  };
}

export function A2pSettings({ org, showToast }) {
  const [reg, setReg]   = useState(null);   // { status, missing }
  const [site, setSite] = useState(null);   // { subdomain, published }
  const [form, setForm] = useState(null);   // business_info
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  async function load() {
    try {
      const [r, rows] = await Promise.all([
        sb.fn("submit-a2p-registration", { action: "get" }),
        fetch(`${SUPABASE_URL}/rest/v1/sites?select=subdomain,published,business_name,phone,email&limit=1`, { headers: authHeaders() }).then(x => x.json()).catch(() => []),
      ]);
      const s = Array.isArray(rows) ? rows[0] : null;
      setSite(s || {});
      setReg(r || { status: "not_started", missing: [] });
      setForm(prefill(r?.business_info || {}, s, org));
    } catch { setReg({ status: "not_started", missing: [] }); setForm(prefill({}, null, org)); }
  }
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save(silent) {
    setBusy(true); setErr("");
    try { const r = await sb.fn("submit-a2p-registration", { action: "save", business_info: form }); setReg(x => ({ ...x, status: "collected", missing: r?.missing || [] })); if (!silent) showToast && showToast("Saved"); }
    catch { setErr("Couldn't save — try again."); }
    setBusy(false);
  }
  async function submit() {
    setBusy(true); setErr("");
    try {
      const r = await sb.fn("submit-a2p-registration", { action: "submit", business_info: form });
      if (r?.error) setErr(r.error);
      else { showToast && showToast("Submitted for A2P approval ✓"); await load(); }
    } catch { setErr("Couldn't submit — try again."); }
    setBusy(false);
  }

  if (!reg || !form) return <div className="card"><div style={{ padding: 22, color: "var(--text2)" }}>Loading…</div></div>;

  const base = site?.subdomain ? `https://${site.subdomain}.${EMBED_HOST}` : null;
  const published = !!site?.subdomain && !!site?.published;
  const submitted = ["submitted", "brand_pending", "campaign_pending", "approved"].includes(reg.status);
  const isSoleProp = form.business_type === "sole_proprietor";

  return (
    <div className="a2p">
      <style>{A2P_CSS}</style>
      <div className="a2p-wrap">
        <div className="a2p-hero">
          <span className="a2p-eyebrow">📱 Texting</span>
          <div className="a2p-h1">Get approved to text your clients</div>
          <p className="a2p-lede">US carriers block business texts from unregistered numbers. This one-time registration (A2P 10DLC) gets your business cleared so your appointment, reminder, and ETA texts actually deliver. We submit it for you.</p>
          <span className={`a2p-pill ${submitted ? "on" : "off"}`}>
            {reg.status === "approved" ? "● Approved" : submitted ? "● Submitted — in review" : "○ Not submitted"}
          </span>
        </div>

        {/* readiness: the compliance pages from their website */}
        <div className="a2p-ready">
          <div className="a2p-label">Your compliance pages</div>
          {published ? (
            <div className="a2p-urls">
              <a href={`${base}/privacy`} target="_blank" rel="noreferrer">✓ Privacy Policy</a>
              <a href={`${base}/terms`} target="_blank" rel="noreferrer">✓ Terms of Service</a>
              <a href={base} target="_blank" rel="noreferrer">✓ Opt-in form (your site)</a>
              <div className="a2p-hint" style={{ marginTop: 8 }}>Auto-generated on your site with the carrier-required wording. Registration uses these URLs.</div>
            </div>
          ) : (
            <div className="a2p-warn">⚠ Publish your website first (<b>Settings → Website</b>). A2P registration needs your Privacy Policy, Terms, and opt-in form hosted on your own site — publishing your iScoopCRM site creates all three automatically.</div>
          )}
        </div>

        {submitted && (
          <div className="a2p-note">✓ Your registration is {reg.status === "approved" ? "approved — you're cleared to text." : "submitted and in carrier review (typically 1–4 weeks). We'll update the status here."}</div>
        )}

        {/* registration path — sole-prop vs registered business, tradeoffs spelled out so the operator chooses informed */}
        <div className="a2p-label" style={{ marginTop: 22 }}>Choose your registration type</div>
        <div className="a2p-paths">
          {[
            { id: "sole", title: "Sole proprietor", sub: "No EIN needed", rows: ["~$4 one-time fee", "1 phone number", "~1,000 texts/day to T-Mobile", "Verified fast — by a text to your phone"], best: "Best if you're solo or just starting out." },
            { id: "registered", title: "Registered business", sub: "LLC / Corp with an EIN", rows: ["~$48 one-time fee", "Multiple phone numbers", "High volume — scales with you", "Carrier review takes 1–4 weeks"], best: "Best if you're growing or higher-volume." },
          ].map(p => {
            const active = (p.id === "sole") === isSoleProp;
            return (
              <button key={p.id} type="button" className={`a2p-path${active ? " on" : ""}`}
                onClick={() => set("business_type", p.id === "sole" ? "sole_proprietor" : (isSoleProp ? "llc" : form.business_type))}>
                <div className="a2p-path-h">
                  <span className="a2p-path-dot">{active ? "●" : "○"}</span>
                  <div><div className="a2p-path-t">{p.title}</div><div className="a2p-path-s">{p.sub}</div></div>
                </div>
                <ul className="a2p-path-rows">{p.rows.map((r, i) => <li key={i}>{r}</li>)}</ul>
                <div className="a2p-path-best">{p.best}</div>
              </button>
            );
          })}
        </div>

        {/* intake form */}
        <div className="a2p-label" style={{ marginTop: 22 }}>Your business details</div>
        <div className="a2p-grid">
          <Field label="Legal business name" k="legal_business_name" form={form} onSet={set} ph="Exactly as registered" wide />
          {!isSoleProp && (
            <div className="a2p-f">
              <label>Business type</label>
              <select value={form.business_type} onChange={e => set("business_type", e.target.value)}>
                {BIZ_TYPES.filter(([v]) => v !== "sole_proprietor").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}
          {!isSoleProp && <Field label="EIN (federal tax ID)" k="ein" form={form} onSet={set} ph="12-3456789" />}
          <div className="a2p-f">
            <label>Industry</label>
            <select value={form.business_industry} onChange={e => set("business_industry", e.target.value)}>
              {INDUSTRIES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <Field label="Business website" k="website_url" form={form} onSet={set} ph="https://yourbiz…" wide hint="Must be the site hosting your Privacy Policy & Terms (your iScoopCRM site is perfect)." />
          <Field label="Street address" k="address_street" form={form} onSet={set} ph="123 Main St" wide />
          <Field label="City" k="address_city" form={form} onSet={set} ph="Haddonfield" />
          <Field label="State" k="address_state" form={form} onSet={set} ph="NJ" />
          <Field label="ZIP" k="address_postal" form={form} onSet={set} ph="08033" />
          <Field label="Authorized rep — name" k="rep_name" form={form} onSet={set} ph="Your name" wide />
          <Field label="Rep email" k="rep_email" form={form} onSet={set} ph="you@biz.com" type="email" />
          <Field label="Rep phone" k="rep_phone" form={form} onSet={set} ph="(555) 123-4567" type="tel" hint={isSoleProp ? "Twilio texts this number a code to verify your registration." : undefined} />
        </div>

        <div className="a2p-label" style={{ marginTop: 20 }}>What you'll text (pre-filled — edit if needed)</div>
        <div className="a2p-grid">
          <TextArea label="What you text customers about" k="use_case_description" form={form} onSet={set} />
          <TextArea label="Sample message 1" k="sample_message_1" form={form} onSet={set} hint="Must name your business and include opt-out (STOP)." />
          <TextArea label="Sample message 2" k="sample_message_2" form={form} onSet={set} />
          <TextArea label="How customers opt in" k="opt_in_description" form={form} onSet={set} />
        </div>

        {err && <div className="a2p-err">{err}</div>}

        <div className="a2p-actions">
          <button className="btn btn-ghost" disabled={busy} onClick={() => save(false)}>Save draft</button>
          <button className="btn btn-gold" disabled={busy || !published} onClick={submit}>
            {busy ? "Submitting…" : submitted ? "Re-submit" : "Submit for approval"}
          </button>
          {!published && <span className="a2p-hint" style={{ alignSelf: "center" }}>Publish your website to enable submission.</span>}
        </div>
      </div>
    </div>
  );
}

const A2P_CSS = `
.a2p-wrap{max-width:660px}
.a2p-hero{margin-bottom:20px}
.a2p-eyebrow{display:inline-block;font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--accent);margin-bottom:11px}
.a2p-h1{font-size:24px;font-weight:700;letter-spacing:-.025em;line-height:1.15;color:var(--text);max-width:20ch}
.a2p-lede{font-size:14px;color:var(--text2);line-height:1.55;margin:11px 0 13px;max-width:56ch}
.a2p-pill{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;padding:4px 11px;border-radius:999px}
.a2p-pill.off{color:var(--text2);background:rgba(0,0,0,.05)}
.a2p-pill.on{color:var(--ok);background:rgba(26,138,82,.12)}
.a2p-label{font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:12px}
.a2p-ready{background:var(--bg);border:1px solid var(--border);border-radius:var(--r,12px);padding:14px 16px;margin-bottom:14px}
.a2p-urls{display:flex;flex-wrap:wrap;gap:8px 16px}
.a2p-urls a{font-size:13px;font-weight:600;color:var(--ok);text-decoration:none}
.a2p-warn{font-size:12.5px;color:#974f0c;background:#fffae6;border:1px solid #ffe7a3;border-radius:8px;padding:11px 13px;line-height:1.5}
.a2p-note{font-size:12.5px;color:var(--ok);background:rgba(26,138,82,.08);border:1px solid rgba(26,138,82,.2);border-radius:8px;padding:11px 13px;margin-bottom:6px;line-height:1.5}
.a2p-grid{display:grid;grid-template-columns:1fr 1fr;gap:13px}
.a2p-f{display:flex;flex-direction:column}
.a2p-f.wide{grid-column:1 / -1}
.a2p-f label{font-size:12px;font-weight:600;color:var(--text);margin-bottom:5px}
.a2p-f input,.a2p-f select,.a2p-f textarea{width:100%;padding:9px 11px;border:1px solid var(--border);border-radius:var(--r-sm,8px);font-size:13.5px;box-sizing:border-box;background:var(--surface);color:var(--text);font-family:inherit}
.a2p-f textarea{resize:vertical;line-height:1.5}
.a2p-f input:focus,.a2p-f select:focus,.a2p-f textarea:focus{outline:none;border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-weak)}
.a2p-hint{font-size:11.5px;color:var(--text2);margin-top:5px;line-height:1.4}
.a2p-err{font-size:12.5px;color:var(--danger);background:rgba(215,50,43,.07);border:1px solid rgba(215,50,43,.25);border-radius:8px;padding:10px 13px;margin-top:14px;line-height:1.45}
.a2p-actions{display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-top:18px}
.a2p-paths{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:4px}
.a2p-path{text-align:left;background:var(--surface);border:1px solid var(--border);border-radius:var(--r,12px);padding:14px 15px;cursor:pointer;transition:.12s;font-family:inherit}
.a2p-path:hover{border-color:var(--text3)}
.a2p-path.on{border-color:var(--accent);box-shadow:0 0 0 1px var(--accent)}
.a2p-path-h{display:flex;align-items:center;gap:9px;margin-bottom:10px}
.a2p-path-dot{font-size:13px;color:var(--accent)}
.a2p-path-t{font-size:14px;font-weight:700;color:var(--text)}
.a2p-path-s{font-size:11.5px;color:var(--text2)}
.a2p-path-rows{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:5px}
.a2p-path-rows li{font-size:12px;color:var(--text2);padding-left:14px;position:relative;line-height:1.35}
.a2p-path-rows li:before{content:"·";position:absolute;left:4px;color:var(--text3)}
.a2p-path-best{font-size:11.5px;color:var(--accent);font-weight:600;margin-top:10px}
/* phones: one column — inside the padded settings pane two columns were ~120px each (City/State/ZIP, EIN, the Industry select all clipped) */
@media(max-width:768px){
  .a2p-paths,.a2p-grid{grid-template-columns:minmax(0,1fr)}
  .a2p-f{min-width:0}
  .a2p-h1{max-width:none}
  .a2p-actions .btn{flex:1 1 140px;justify-content:center}
  .a2p-actions .a2p-hint{flex-basis:100%}
  .a2p-urls{flex-direction:column;gap:2px}
  .a2p-urls a{display:block;padding:8px 0;min-height:36px;box-sizing:border-box}
}
`;
